import { createContext, useContext, useCallback, useMemo, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { apiClient, ApiError } from '@/shared/lib/api-client'
import { clearPersistedCache } from '@/shared/lib/query-client'
import type { AuthUser } from '@app/shared/types/entities'

type AuthContextValue = {
  user: AuthUser | null
  isLoading: boolean
  /** True when auth failed due to network error (not 401). Cached user may still be available. */
  isNetworkError: boolean
  logout: () => Promise<void>
  refetch: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }): React.ReactElement {
  const queryClient = useQueryClient()

  const {
    data,
    isLoading,
    isError,
    error,
    refetch: refetchAuth,
  } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async (): Promise<AuthUser | null> => {
      try {
        const res = await apiClient.get<{ user: AuthUser }>('/api/auth/me')
        return res.user
      } catch (err) {
        // Server explicitly said unauthorized — genuinely no session
        if (err instanceof ApiError && err.status === 401) {
          return null
        }
        // Network error / server down — throw so React Query keeps cached data
        throw err
      }
    },
    retry: false,
    staleTime: Infinity,
  })

  // Network error means auth check failed but NOT because of 401.
  // If there's cached auth data from IndexedDB, it's still in `data`.
  const isNetworkError = isError && !(error instanceof ApiError && error.status === 401)

  const user = data ?? null

  const logout = useCallback(async (): Promise<void> => {
    try {
      await apiClient.post('/api/auth/logout')
    } catch {
      // Ignore logout errors
    } finally {
      clearPersistedCache()
      queryClient.setQueryData(['auth', 'me'], null)
    }
  }, [queryClient])

  const refetch = useCallback((): void => {
    refetchAuth()
  }, [refetchAuth])

  const value = useMemo<AuthContextValue>(
    () => ({ user, isLoading, isNetworkError, logout, refetch }),
    [user, isLoading, isNetworkError, logout, refetch],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}
